<script setup lang="ts">
const route = useRoute()
const { locale } = useI18n()

const runtimeConfig = useRuntimeConfig()

const previewSecret = runtimeConfig?.previewSecret || runtimeConfig?.public?.previewSecret
const isPreview = computed(() => route?.query?.previewSecret === previewSecret)

const ENTRIES_LIMIT = 100

interface PersonOccurrencesData {
  docs: PersonOccurrence[]
  totalDocs: number
  hasNextPage: boolean
}

interface GuestPerson {
  id: string
  firstName: string
  lastName: string
  isExternal: boolean
  person?: { id: string; slug: string }
}

interface GuestDoc {
  id: string
  title: string
  dateStart: string
  dateEnd: string
  people: GuestPerson[]
}

interface GuestsData {
  docs: GuestDoc[]
  totalDocs: number
  hasNextPage: boolean
}

const { data: pageData } = await useAsyncData(
  () => `people-page-${locale.value}`,
  () => $fetch('/api/payload-gql', {
    method: 'POST',
    body: {
      query: GlobalPeoplePageQuery,
      variables: { locale: locale.value, isPreview: isPreview.value },
      isPreview: isPreview.value,
    },
  })
)

// current = still running today (an occurrence ending today still counts),
// compared on the date rather than the calendar year so a period that ended
// earlier this year already belongs to the archive
const now = new Date()
const startOfToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString()

const occurrencesWhere = {
  type: { in: ['participant', 'tutor', 'staff'] },
  // an ongoing role has no end date to compare against, so it qualifies as
  // current on the flag alone -- otherwise every permanent tutor would drop
  // off this list once their (nominal) end date has passed
  OR: [
    { dateEnd: { greater_than_equal: startOfToday } },
    { isOngoing: { equals: true } },
  ],
}

const guestsWhere = {
  dateEnd: { greater_than_equal: startOfToday },
}

const previewStatuses = computed(() => (isPreview.value ? ['draft', 'published'] : ['published']))

const [{ data: entries, error }, { data: guests }] = await Promise.all([
  useAsyncData<PersonOccurrencesData>(
    () => `people-current-entries-${locale.value}`,
    () => $fetch<PersonOccurrencesData>('/api/payload-gql', {
      method: 'POST',
      body: {
        query: ManyCurrentPersonOccurrencesQuery,
        variables: {
          page: 1,
          limit: ENTRIES_LIMIT,
          locale: locale.value,
          where: occurrencesWhere,
          isPreview: isPreview.value,
          statuses: previewStatuses.value,
        },
        isPreview: isPreview.value,
      },
    })
  ),
  useAsyncData<GuestsData>(
    () => `people-current-guests-${locale.value}`,
    () => $fetch<GuestsData>('/api/payload-gql', {
      method: 'POST',
      body: {
        query: ManyCurrentGuestsQuery,
        variables: {
          locale: locale.value,
          where: guestsWhere,
          isPreview: isPreview.value,
          statuses: previewStatuses.value,
        },
        isPreview: isPreview.value,
      },
    })
  ),
])

if (error.value) {
  throw createError({ statusCode: 500, statusMessage: 'Error fetching page' })
}

usePageSeo({
  title: () => pageData.value?.title,
})

const extraDocs = ref<PersonOccurrence[]>([])
const allDocs = computed(() => [
  ...(entries.value?.docs ?? []),
  ...extraDocs.value,
])

onMounted(async () => {
  if (!entries.value?.hasNextPage) return
  let p = 2
  while (true) {
    const result = await $fetch<PersonOccurrencesData>('/api/payload-gql', {
      method: 'POST',
      body: {
        query: ManyCurrentPersonOccurrencesQuery,
        variables: {
          page: p,
          limit: ENTRIES_LIMIT,
          locale: locale.value,
          where: occurrencesWhere,
          isPreview: isPreview.value,
          statuses: previewStatuses.value,
        },
        isPreview: isPreview.value,
      },
    })
    extraDocs.value = [...extraDocs.value, ...(result?.docs ?? [])]
    if (!result?.hasNextPage) break
    p++
  }
})
</script>

<template>
  <page-layout>
    <template #header>
      <page-header
        :title="pageData?.title"
        :route="{
          name: 'slug',
          title: pageData?.title,
          slug: pageData?.slug,
        }"
        :include-breadcrumbs="true"
      />
    </template>

    <template #default>
      <pages-people
        v-if="pageData"
        :data="pageData"
        :entries="allDocs"
        :guests="guests?.docs ?? []"
      />

      <!-- a single person opens as an overlay on top of this list -->
      <nuxt-page />
    </template>
  </page-layout>
</template>
